import { PAGES } from './pagePulseConfig';
import type { PulsePayload, PulseRow } from './pagePulseApi';

export type PageFilter = 'all' | (typeof PAGES)[number]['path'];
export interface MetricValue {
  value: string;
  note?: string | undefined;
}
export interface KpiTile extends MetricValue {
  label: string;
}

const number = (row: PulseRow, key: string) => {
  const value = row.metrics[key];
  return typeof value === 'number' ? value : 0;
};
/**
 * A screen copy rides in a 'clarity' row with metrics {} and the copied text
 * under breakdowns.screen (the database allows no other source; Dev3
 * t_26ef68d7). It is not Clarity API data, so every API number below skips it.
 */
const screenOf = (row: PulseRow) => {
  const screen = row.breakdowns.screen;
  return typeof screen === 'object' && screen !== null
    ? (screen as { taken_at?: unknown; items?: unknown })
    : undefined;
};
const isScreenCopy = (row: PulseRow) => screenOf(row) !== undefined;
const rowsFor = (payload: PulsePayload, page: PageFilter, source?: string) =>
  payload.rows.filter(
    (row) =>
      !isScreenCopy(row) &&
      (page === 'all' || row.page_path === page) &&
      (source === undefined || row.source === source),
  );
const sum = (rows: PulseRow[], key: string) =>
  rows.reduce((total, row) => total + number(row, key), 0);
const percent = (part: number, whole: number) =>
  whole === 0 ? '--' : `${Math.round((part / whole) * 100)}%`;
export const shortDay = (value: string | null) =>
  value === null
    ? ''
    : new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
const first = shortDay;

const GA4_METRICS = 'https://support.google.com/analytics/answer/9143382';
const GA4_PAGES = 'https://support.google.com/analytics/answer/12926732';
const GA4_FORMS = 'https://support.google.com/analytics/answer/9216061';
const GA4_FRESHNESS = 'https://support.google.com/analytics/answer/11198161';
const CLARITY_GLOSSARY =
  'https://learn.microsoft.com/en-us/clarity/setup-and-installation/glossary-of-terms';
const CLARITY_DASHBOARD = 'https://learn.microsoft.com/en-us/clarity/insights/dashboard-features';
const CLARITY_INSIGHTS_DOC = 'https://learn.microsoft.com/en-us/clarity/insights/semantic-metrics';
const CLARITY_SCROLL = 'https://learn.microsoft.com/en-us/clarity/heatmaps/scroll-maps';
const CLARITY_EXPORT =
  'https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-data-export-api';

/**
 * What each number means, in GA4's and Clarity's own help wording (Kyu,
 * 5 Oct: "copy it, don't create our own"). Quoted from the page in `source`,
 * shortened to one or two sentences, never rewritten. Edit here, not in the
 * view.
 */
export const PULSE_DEFINITIONS = {
  views: {
    text: 'GA4: The number of mobile app screens or web pages your users saw. Repeated views of a single screen or page are counted.',
    source: GA4_METRICS,
  },
  activeUsers: {
    text: 'GA4: The number of distinct users who visited your website or application.',
    source: GA4_METRICS,
  },
  newUsers: {
    text: 'GA4: The number of new unique user IDs that logged the first_open or first_visit event. The metric allows you to measure the number of users who interacted with your site or launched your app for the first time.',
    source: GA4_METRICS,
  },
  sessions: {
    text: 'GA4: The number of sessions that began on your website or application. By default, a session ends (times out) after 30 minutes of user inactivity.',
    source: GA4_METRICS,
  },
  engagementRate: {
    text: 'GA4: The percentage of sessions that were engaged sessions. Engagement rate = engaged sessions / total sessions.',
    source: GA4_METRICS,
  },
  averageEngagementTime: {
    text: "GA4: The average time that your website was in focus in a user's browser or an app was in the foreground of a user's device. Average engagement time = total user engagement durations / number of active users.",
    source: GA4_METRICS,
  },
  viewsPerActiveUser: {
    text: 'GA4: The average number of mobile app screens or web pages viewed per active user.',
    source: GA4_PAGES,
  },
  pagePath: {
    text: 'GA4: The page path from a website URL and screen class from a mobile app. Page path derives from the value after the domain.',
    source: GA4_PAGES,
  },
  eventName: {
    text: "GA4: 'form_start': the first time a user interacts with a form in a session. 'form_submit': when the user submits a form.",
    source: GA4_FORMS,
  },
  eventCount: {
    text: 'GA4: The number of times users triggered an event.',
    source: GA4_METRICS,
  },
  sourceMedium: {
    text: "GA4: The source and medium that referred the user's session. This dimension describes where users come from when they start a new session.",
    source: GA4_METRICS,
  },
  deviceCategory: {
    text: "GA4: The type of device from which user activity originated. Device categories include 'desktop', 'mobile', and 'tablet'.",
    source: GA4_METRICS,
  },
  country: {
    text: 'GA4: The country from which user activity originated.',
    source: GA4_METRICS,
  },
  partial: {
    text: 'GA4: Data processing can take 24-48 hours. During that time, data in your reports may change.',
    source: GA4_FRESHNESS,
  },
  claritySessions: {
    text: 'Clarity: The amount of time a user spent on your site. A session ends if the user is inactive for 30 minutes.',
    source: CLARITY_GLOSSARY,
  },
  // Microsoft Learn has no sentence for this tile; the dashboard page's own
  // description of its tiles is the closest Clarity wording (findings-dev2.md).
  pagesPerSession: {
    text: 'Clarity: The Clarity dashboard provides aggregate metrics to help you get an overall understanding of the traffic on your site.',
    source: CLARITY_DASHBOARD,
  },
  scrollDepth: {
    text: 'Clarity: Scroll maps help you determine the ideal page length for maximum conversions and show how far your users scroll on the pages.',
    source: CLARITY_SCROLL,
  },
  activeTime: {
    text: 'Clarity: Time a user spends navigating on your site.',
    source: CLARITY_DASHBOARD,
  },
  deadClicks: {
    text: 'Clarity: Click interactions are marked as dead clicks when a user clicks on an element but gets no feedback in a reasonable amount of time.',
    source: CLARITY_INSIGHTS_DOC,
  },
  rageClicks: {
    text: "Clarity: A page view or user session is flagged with 'rage click' if the user clicks multiple times in a clustered area in rapid succession.",
    source: CLARITY_INSIGHTS_DOC,
  },
  excessiveScrolling: {
    text: "Clarity: A user session is flagged as 'excessive scrolling' if that session has higher amounts of vertical scrolling than the expected average.",
    source: CLARITY_INSIGHTS_DOC,
  },
  quickBacks: {
    text: "Clarity: A 'quick back' is a click that leads users away from the current page to another website. The user doesn't find the navigation useful and returns to the original page or website under a certain threshold of time.",
    source: CLARITY_INSIGHTS_DOC,
  },
  javascriptErrors: {
    text: 'Clarity: JavaScript errors occur while executing JavaScript on the browser.',
    source: CLARITY_INSIGHTS_DOC,
  },
  smartEvents: {
    text: 'Clarity: Smart events track key user actions on your website. Clarity automatically defines smart events for you, and you can customize them or define new events.',
    source: CLARITY_INSIGHTS_DOC,
  },
  clarityHistory: {
    text: 'Clarity: Data retrieval is confined to the previous 1 to 3 days.',
    source: CLARITY_EXPORT,
  },
} as const;
export type PulseDefinitionKey = keyof typeof PULSE_DEFINITIONS;

/** Clarity's Insights tiles, in the order the Clarity dashboard shows them. */
export const CLARITY_INSIGHTS: ReadonlyArray<readonly [string, string, PulseDefinitionKey]> = [
  ['Dead clicks', 'dead_clicks', 'deadClicks'],
  ['Rage clicks', 'rage_clicks', 'rageClicks'],
  ['Excessive scrolling', 'excessive_scrolls', 'excessiveScrolling'],
  ['Quick backs', 'quick_backs', 'quickBacks'],
  ['JavaScript errors', 'script_errors', 'javascriptErrors'],
];

/** GA4 writes durations as "31s" or "1m 05s". */
export const ga4Duration = (seconds: number) => {
  const whole = Math.round(seconds);
  return whole < 60
    ? `${whole}s`
    : `${Math.floor(whole / 60)}m ${String(whole % 60).padStart(2, '0')}s`;
};

const niceStep = (raw: number) => {
  const power = 10 ** Math.floor(Math.log10(raw));
  const unit = raw / power;
  return (unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 5 ? 5 : 10) * power;
};
/**
 * The chart's y axis scaled to the real maximum, so nothing is clipped (the
 * old chart drew 30 - min(value, 30) and flattened every day above 30), with
 * at most four gridline steps and first, middle and last dates on the x axis.
 * Coordinates are percent of the plot box: y(0) = 100, y(top) = 0.
 */
export function chartScale(series: ReturnType<typeof seriesPoints>) {
  const values = series.flatMap((line) => line.points.map((point) => point.value));
  const max = Math.max(0, ...values);
  const step = niceStep((max > 0 ? max : 4) / 4);
  const top = step * Math.max(1, Math.ceil(max / step));
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, index) => index * step);
  const days = series[0]?.points.map((point) => point.day) ?? [];
  const x = (index: number) => (index * 100) / Math.max(days.length - 1, 1);
  const marks = [...new Set([0, Math.floor((days.length - 1) / 2), days.length - 1])].filter(
    (index) => index >= 0,
  );
  return {
    top,
    ticks,
    x,
    y: (value: number) => 100 - (value / top) * 100,
    xLabels: marks.map((index) => ({ x: x(index), text: shortDay(days[index] ?? null) })),
  };
}

/**
 * first_day.clarity is min(day) over every Clarity row (migration
 * 20270214000100:60), carriers included. When that day holds only screen
 * copies, the first real Clarity day is the earliest API row we can see.
 */
function clarityFirstDay(payload: PulsePayload): string | null {
  const day = payload.first_day.clarity;
  const sameDay = payload.rows.filter((row) => row.source === 'clarity' && row.day === day);
  if (day === null || sameDay.length === 0 || !sameDay.every(isScreenCopy)) return day;
  return (
    rowsFor(payload, 'all', 'clarity')
      .map((row) => row.day)
      .sort()[0] ?? null
  );
}
const COPY_POST_WINDOW_MS = 30 * 60 * 1000;
const COPY_POST_NOTE = 'not shown, see Copied at';
/**
 * updated_at is max(pulled_at) over every row (migration 20270214000100:59),
 * so each screen-copy post moves it. When it lands within 30 minutes after
 * the newest copy was taken, that post set it: say so instead of a time the
 * API numbers do not have.
 */
export function lastUpdate(payload: PulsePayload): string {
  if (payload.updated_at === null) return 'not yet connected';
  const updated = Date.parse(payload.updated_at);
  const taken = payload.rows
    .map((row) => screenOf(row)?.taken_at)
    .map((value) => (typeof value === 'string' ? Date.parse(value) : Number.NaN))
    .filter((value) => !Number.isNaN(value));
  const newest = Math.max(...taken);
  return taken.length > 0 && updated >= newest && updated - newest <= COPY_POST_WINDOW_MS
    ? COPY_POST_NOTE
    : payload.updated_at;
}
/** "HH:MM" in Manila time, the time the screen was copied. */
export function copiedAt(takenAt: string): string | null {
  const time = new Date(takenAt);
  return Number.isNaN(time.getTime())
    ? null
    : time.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone: 'Asia/Manila',
      });
}
export interface ScreenItem {
  source: string;
  tab: string;
  label: string;
  value: string;
  note?: string | undefined;
  /** Vidalytics: the video the item belongs to (none = account totals). */
  video?: string | undefined;
  /** An image to show (a Drive link); anything else is only a slot for now. */
  image?: string | undefined;
}
export interface ScreenCopy {
  page: string;
  takenAt: string;
  items: ScreenItem[];
}
const text = (value: unknown) => (typeof value === 'string' ? value : '');
/** Today's screen copy per page, in the page picker's order. */
export function screenCopies(payload: PulsePayload, page: PageFilter): ScreenCopy[] {
  return PAGES.flatMap((item) => {
    if (page !== 'all' && page !== item.path) return [];
    const row = payload.rows.find(
      (candidate) =>
        candidate.day === payload.to &&
        candidate.page_path === item.path &&
        isScreenCopy(candidate),
    );
    const screen = row === undefined ? undefined : screenOf(row);
    if (screen === undefined) return [];
    const items = (Array.isArray(screen.items) ? (screen.items as unknown[]) : [])
      .filter(
        (entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null,
      )
      .map((entry) => ({
        source: text(entry.source),
        tab: text(entry.tab),
        label: text(entry.label),
        value: text(entry.value),
        note: typeof entry.note === 'string' ? entry.note : undefined,
        video: typeof entry.video === 'string' ? entry.video : undefined,
        image: typeof entry.image === 'string' ? entry.image : undefined,
      }))
      .filter((entry) => entry.label !== '');
    return [{ page: item.path, takenAt: text(screen.taken_at), items }];
  });
}
const isSmartEvent = (item: ScreenItem) => /smart event/i.test(item.tab);
export interface ScreenImage {
  label: string;
  value: string;
  src: string;
}
export interface ScreenGroup {
  tab: string;
  tiles: KpiTile[];
  images: ScreenImage[];
}
/** Items grouped by their `tab`, in the order first copied; images apart from tiles. */
function groupByTab(items: ScreenItem[]): ScreenGroup[] {
  const groups = new Map<string, ScreenGroup>();
  for (const item of items) {
    const group = groups.get(item.tab) ?? { tab: item.tab, tiles: [], images: [] };
    groups.set(item.tab, group);
    if (item.image === undefined)
      group.tiles.push({ label: item.label, value: item.value, note: item.note });
    else group.images.push({ label: item.label, value: item.value, src: item.image });
  }
  return [...groups.values()];
}
/**
 * The copied Clarity Dashboard, laid out the way Clarity's screen shows it
 * (BRIEF.md section 6, REF-clarity-dashboard-20261005.jpg): four top cards,
 * Users overview, Insights in Clarity's order, then Smart events. Whatever
 * the fixed boxes have no place for comes back in `more`, by tab, so nothing
 * that was copied is left out (Kyu, 6 Oct).
 */
export function clarityScreen(items: ScreenItem[]) {
  const clarity = items.filter((item) => item.source === 'clarity');
  const cards = clarity.filter((item) => !isSmartEvent(item));
  const used = new Set<ScreenItem>();
  const pick = (labels: string[]): KpiTile[] =>
    labels.map((label) => {
      const found = cards.find(
        (item) =>
          item.image === undefined &&
          !used.has(item) &&
          item.label.trim().toLowerCase() === label.toLowerCase(),
      );
      if (found !== undefined) used.add(found);
      return { label, value: found?.value ?? '--', note: found?.note };
    });
  return {
    top: pick(['Sessions', 'Pages per session', 'Scroll depth', 'Active time spent']),
    users: pick([
      'Live users',
      'Unique users',
      'Sessions with new users',
      'Sessions with returning users',
    ]),
    insights: pick(['Rage clicks', 'Dead clicks', 'Excessive scrolling', 'Quick backs']),
    smartEvents: clarity
      .filter(isSmartEvent)
      .map((item): [string, string] => [item.label, item.value]),
    more: groupByTab(cards.filter((item) => !used.has(item))),
  };
}
export type PulseTab = 'ga4' | 'clarity' | 'vidalytics';
/** The newest copy time (as written) over today's screen copies that hold `source` items. */
function newestCopy(payload: PulsePayload, source: string): string | null {
  const times = screenCopies(payload, 'all')
    .filter((copy) => copy.items.some((item) => item.source === source))
    .map((copy) => copy.takenAt)
    .filter((value) => !Number.isNaN(Date.parse(value)));
  return times.sort((a, b) => Date.parse(b) - Date.parse(a))[0] ?? null;
}
/** "Oct 7, 2:30 AM PHT" in Manila time, or null when the time cannot be read. */
function manilaStamp(value: string): string | null {
  const time = new Date(value);
  if (Number.isNaN(time.getTime())) return null;
  const part = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Manila',
    })
      .formatToParts(time)
      .map((entry) => [entry.type, entry.value]),
  );
  return `${part.month} ${part.day}, ${part.hour}:${part.minute} ${part.dayPeriod} PHT`;
}
/**
 * The one line under each tab. Clarity and Vidalytics use their screen copy
 * times. Without per-source times in page_pulse_payload, GA4 uses updated_at
 * unless it marks a copy post; then only a copy carrying GA4 items gives a
 * provable GA4 read time.
 */
export function updatedAsOf(payload: PulsePayload, tab: PulseTab): string {
  const none = 'Not copied yet';
  let stamp: string | null;
  if (tab === 'ga4') {
    if (rowsFor(payload, 'all', 'ga4').length === 0) return none;
    stamp =
      lastUpdate(payload) === COPY_POST_NOTE ? newestCopy(payload, 'ga4') : payload.updated_at;
  } else stamp = newestCopy(payload, tab);
  const text = stamp === null ? null : manilaStamp(stamp);
  return text === null ? none : `Updated as of ${text}`;
}
/**
 * The copied Vidalytics screen: account totals first (items with no video),
 * then one block per video in the order copied. Vidalytics items ride in the
 * screen carrier rows, told apart by their source (Part A, snapshot poster).
 */
export function vidalyticsScreen(payload: PulsePayload) {
  const items = screenCopies(payload, 'all')
    .flatMap((copy) => copy.items)
    .filter((item) => item.source === 'vidalytics');
  const videos = new Map<string, ScreenItem[]>();
  for (const item of items)
    if (item.video !== undefined) videos.set(item.video, [...(videos.get(item.video) ?? []), item]);
  return {
    takenAt: newestCopy(payload, 'vidalytics'),
    account: groupByTab(items.filter((item) => item.video === undefined)),
    videos: [...videos].map(([video, rows]) => ({ video, groups: groupByTab(rows) })),
  };
}
/** The copied GA4 numbers, one group per GA4 report, in the order copied. */
export function ga4Screen(items: ScreenItem[]) {
  const groups = new Map<string, KpiTile[]>();
  for (const item of items.filter((entry) => entry.source === 'ga4'))
    groups.set(item.tab, [
      ...(groups.get(item.tab) ?? []),
      { label: item.label, value: item.value, note: item.note },
    ]);
  return [...groups].map(([tab, tiles]) => ({ tab, tiles }));
}

/**
 * The one line under the Clarity heading: when Clarity data starts and that
 * Clarity's export only reaches back 1 to 3 days, so history starts at the
 * first daily pull. Also says so when rows arrived but carry no session count.
 */
export function clarityHistoryNote(payload: PulsePayload): string {
  const firstDay = clarityFirstDay(payload);
  if (firstDay === null) return 'No Clarity data yet.';
  const rows = rowsFor(payload, 'all', 'clarity');
  const empty =
    rows.length > 0 && sum(rows, 'sessions') === 0
      ? ' Clarity rows reached the Hub but carry no session count, so the tiles read 0.'
      : '';
  return `Clarity data starts ${first(firstDay)}. Clarity's export only gives the last 1 to 3 days, so older days cannot be filled in.${empty}`;
}

export function selectedRows(payload: PulsePayload, page: PageFilter): PulseRow[] {
  return rowsFor(payload, page);
}
export function total(
  payload: PulsePayload,
  page: PageFilter,
  key: string,
  source?: string,
): number {
  return sum(rowsFor(payload, page, source), key);
}
export function formStarts(payload: PulsePayload, page: PageFilter): MetricValue {
  const starts = total(payload, page, 'form_start', 'ga4');
  return starts === 0
    ? { value: '--', note: 'form starts not recorded yet' }
    : { value: String(starts) };
}
export function pulseMetrics(payload: PulsePayload, page: PageFilter): Record<string, MetricValue> {
  const ga4 = rowsFor(payload, page, 'ga4');
  const clarity = rowsFor(payload, page, 'clarity');
  const sessions = sum(ga4, 'sessions');
  const starts = sum(ga4, 'form_start');
  const firstClarity = clarityFirstDay(payload);
  const clarityNote =
    clarity.length === 0
      ? 'Clarity not connected yet'
      : firstClarity !== null && firstClarity > payload.from
        ? `since ${first(firstClarity)}`
        : undefined;
  const clarityValue = (value: number, suffix = ''): MetricValue =>
    clarity.length === 0
      ? { value: '--', note: clarityNote }
      : { value: `${value}${suffix}`, note: clarityNote };
  return {
    views: { value: String(sum(ga4, 'screenPageViews')) },
    sessions: { value: String(sessions) },
    submits: { value: String(sum(ga4, 'form_submit')) },
    newUsers: { value: String(sum(ga4, 'newUsers')), note: 'daily visitors, summed' },
    engagement: { value: percent(sum(ga4, 'engagedSessions'), sessions) },
    bounce: { value: percent(sessions - sum(ga4, 'engagedSessions'), sessions) },
    dropoff:
      starts === 0
        ? { value: '--', note: 'form starts not recorded yet' }
        : { value: percent(starts - sum(ga4, 'form_submit'), starts) },
    scroll: clarityValue(
      clarity.length === 0
        ? 0
        : Math.round(sum(clarity, 'scroll_depth_sum') / Math.max(sum(clarity, 'sessions'), 1)),
      '%',
    ),
    frustration: clarityValue(sum(clarity, 'rage_clicks') + sum(clarity, 'dead_clicks')),
    quickBacks: clarityValue(sum(clarity, 'quick_backs')),
  };
}
export function gaKpiTiles(payload: PulsePayload, page: PageFilter): KpiTile[] {
  const metrics = pulseMetrics(payload, page);
  const activeUsers = total(payload, page, 'activeUsers', 'ga4');
  const sessions = total(payload, page, 'sessions', 'ga4');
  const engaged = total(payload, page, 'engagedSessions', 'ga4');
  return [
    { label: 'Views', value: metrics.views?.value ?? '--' },
    { label: 'Active users', value: String(activeUsers), note: 'daily visitors, summed' },
    { label: 'New users', value: metrics.newUsers?.value ?? '--', note: metrics.newUsers?.note },
    { label: 'Sessions', value: metrics.sessions?.value ?? '--' },
    {
      label: 'Engagement rate',
      value: sessions === 0 ? '--' : `${((engaged / sessions) * 100).toFixed(1)}%`,
    },
    {
      label: 'Average engagement time per active user',
      value: ga4Duration(
        total(payload, page, 'userEngagementDuration', 'ga4') / Math.max(activeUsers, 1),
      ),
    },
  ];
}
/** Which definition explains each tile, by label. */
export const TILE_DEFINITIONS: Record<string, PulseDefinitionKey> = {
  Views: 'views',
  'Active users': 'activeUsers',
  'New users': 'newUsers',
  Sessions: 'sessions',
  'Engagement rate': 'engagementRate',
  'Average engagement time per active user': 'averageEngagementTime',
  'Pages per session': 'pagesPerSession',
  'Scroll depth': 'scrollDepth',
  'Active time spent': 'activeTime',
};
export function clarityTiles(payload: PulsePayload, page: PageFilter): KpiTile[] {
  const metrics = pulseMetrics(payload, page);
  const sessions = total(payload, page, 'sessions', 'clarity');
  const connected = rowsFor(payload, page, 'clarity').length > 0;
  const average = connected ? 'average' : metrics.scroll?.note;
  const totalTime = total(payload, page, 'total_time_sum', 'clarity') / Math.max(sessions, 1);
  return [
    { label: 'Sessions', value: String(sessions), note: connected ? undefined : average },
    {
      label: 'Pages per session',
      value: (
        total(payload, page, 'pages_per_session_sum', 'clarity') / Math.max(sessions, 1)
      ).toFixed(1),
      note: average,
    },
    { label: 'Scroll depth', value: metrics.scroll?.value ?? '--', note: average },
    {
      label: 'Active time spent',
      value: ga4Duration(
        total(payload, page, 'active_time_sum', 'clarity') / Math.max(sessions, 1),
      ),
      note: connected && totalTime > 0 ? `out of ${ga4Duration(totalTime)} total` : average,
    },
  ];
}
export function sourceRows(payload: PulsePayload, page: PageFilter) {
  const merged = new Map<string, number>();
  for (const row of rowsFor(payload, page, 'ga4'))
    for (const [label, value] of Object.entries(
      (row.breakdowns.source_medium ?? {}) as Record<string, unknown>,
    ))
      merged.set(label, (merged.get(label) ?? 0) + (typeof value === 'number' ? value : 0));
  return [...merged]
    .map(([label, sessions]) => ({
      label:
        label === '4oceanpartnerships.com / referral' ? 'partnerships domain (redirect)' : label,
      sessions,
    }))
    .sort((a, b) => b.sessions - a.sessions)
    .slice(0, 8);
}
export const sourceBarShare = (sessions: number, totalSessions: number) =>
  totalSessions === 0 ? 0 : Math.round((sessions / totalSessions) * 100);
export function dmTable(
  payload: PulsePayload,
  page: PageFilter,
  versions: readonly { label: string; utmContent: string }[],
) {
  const merged = new Map<string, { sessions: number; form_submit: number }>();
  for (const row of rowsFor(payload, page, 'ga4'))
    for (const [key, value] of Object.entries(
      (row.breakdowns.dm ?? {}) as Record<string, unknown>,
    )) {
      const entry = value as Record<string, unknown>;
      const current = merged.get(key) ?? { sessions: 0, form_submit: 0 };
      current.sessions += typeof entry.sessions === 'number' ? entry.sessions : 0;
      current.form_submit += typeof entry.form_submit === 'number' ? entry.form_submit : 0;
      merged.set(key, current);
    }
  const rows = versions.map((version) => ({
    ...version,
    ...(merged.get(version.utmContent) ?? { sessions: 0, form_submit: 0 }),
  }));
  const live = rows.some((row) => row.utmContent !== '' && merged.has(row.utmContent));
  const top = Math.max(...rows.map((row) => row.form_submit));
  return {
    live,
    rows: rows.map((row) => ({ ...row, leading: live && top > 0 && row.form_submit === top })),
  };
}
export function seriesPoints(
  payload: PulsePayload,
  page: PageFilter,
  batchDays: readonly string[],
) {
  const pages = page === 'all' ? PAGES.map((item) => item.path) : [page];
  const days = [...new Set(rowsFor(payload, 'all').map((row) => row.day))].sort();
  return pages.map((path) => ({
    path,
    points: days.map((day) => ({
      day,
      value: payload.rows
        .filter((row) => row.day === day && row.page_path === path && row.source === 'ga4')
        .reduce((n, row) => n + number(row, 'screenPageViews'), 0),
      batch: batchDays.includes(day),
    })),
  }));
}
